/**
 * =============================================
 * IMPRESSAO DE RELATORIO DE CONFERENCIA
 * =============================================
 * Abre uma nova janela com o relatorio formatado em HTML
 * e chama window.print(). Usuario pode salvar como PDF
 * pela dialog de impressao do navegador.
 * =============================================
 */
import type { RelatorioExportRow } from '../services/montagemPalete';

interface PrintOptions {
  data: RelatorioExportRow[];
  periodoInicio?: string;
  periodoFim?: string;
  filtros?: {
    cliente?: string;
    numero_pedido?: string;
    busca?: string;
  };
}

const RESULTADO_LABELS: Record<string, string> = {
  ok: 'OK',
  duplicado: 'Duplicado',
  removido: 'Removido',
  nao_encontrado: 'Nao encontrado',
  avaria: 'Avaria',
  erro: 'Erro'
};

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDateTime(iso: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

function formatDate(iso?: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

export function imprimirRelatorioConferencia(options: PrintOptions) {
  const { data, periodoInicio, periodoFim, filtros } = options;

  const win = window.open('', '_blank', 'width=1200,height=800');
  if (!win) {
    alert('Nao foi possivel abrir a janela de impressao. Verifique o bloqueador de pop-ups.');
    return;
  }

  // Agrupar por palete > pedido > etiquetas
  const paletesMap = new Map<string, {
    numero_palete: number | null;
    cliente: string | null;
    numero_operacao: number | null;
    area: string;
    status_palete: string | null;
    status_operacao: string | null;
    pedidos: Map<string, RelatorioExportRow[]>;
  }>();

  for (const row of data) {
    const paleteKey = String(row.numero_palete ?? 'sem-palete');
    if (!paletesMap.has(paleteKey)) {
      paletesMap.set(paleteKey, {
        numero_palete: row.numero_palete,
        cliente: row.cliente,
        numero_operacao: row.numero_operacao,
        area: [row.area_codigo, row.area_nome].filter(Boolean).join(' - '),
        status_palete: row.status_palete,
        status_operacao: row.status_operacao,
        pedidos: new Map()
      });
    }
    const palete = paletesMap.get(paleteKey)!;
    const pedidoKey = row.numero_pedido || 'Sem Pedido';
    if (!palete.pedidos.has(pedidoKey)) {
      palete.pedidos.set(pedidoKey, []);
    }
    palete.pedidos.get(pedidoKey)!.push(row);
  }

  const dataAtual = new Date().toLocaleString('pt-BR');
  const periodoStr = periodoInicio || periodoFim
    ? `${formatDate(periodoInicio)} a ${formatDate(periodoFim)}`
    : 'Sem filtro de periodo';

  const totalPaletes = paletesMap.size;
  const totalLeituras = data.length;
  const totalPedidos = new Set(data.map(d => d.numero_pedido).filter(Boolean)).size;

  const paletesHtml = Array.from(paletesMap.values()).map(palete => {
    const totalPaleteLeituras = Array.from(palete.pedidos.values()).reduce((s, arr) => s + arr.length, 0);
    const pedidosHtml = Array.from(palete.pedidos.entries()).map(([pedido, etiquetas]) => `
      <div class="pedido-bloco">
        <div class="pedido-header">
          <strong>Pedido: ${escapeHtml(pedido)}</strong>
          <span class="pedido-count">${etiquetas.length} leitura(s)</span>
        </div>
        <table class="etiquetas-table">
          <thead>
            <tr>
              <th>Codigo</th>
              <th>Volume</th>
              <th>Status</th>
              <th>Tipo</th>
              <th>Conferente</th>
              <th>Data/Hora</th>
            </tr>
          </thead>
          <tbody>
            ${etiquetas.map(e => `
              <tr>
                <td class="mono">${escapeHtml(e.codigo_lido || '-')}</td>
                <td>${e.volume_atual ? escapeHtml(e.volume_atual) + (e.total_volumes ? '/' + escapeHtml(e.total_volumes) : '') : '-'}</td>
                <td>${escapeHtml(RESULTADO_LABELS[e.resultado] || e.resultado)}</td>
                <td>${escapeHtml(e.tipo_leitura || '-')}</td>
                <td>${escapeHtml(e.conferente_nome || '-')}</td>
                <td>${formatDateTime(e.criado_em)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `).join('');

    return `
      <div class="palete-bloco">
        <div class="palete-header">
          <h3>Palete #${palete.numero_palete ?? '-'}</h3>
          <div class="palete-meta">
            <span><strong>Cliente:</strong> ${escapeHtml(palete.cliente || '-')}</span>
            <span><strong>Operacao:</strong> #${palete.numero_operacao ?? '-'}</span>
            <span><strong>Area:</strong> ${escapeHtml(palete.area || '-')}</span>
            <span><strong>Leituras:</strong> ${totalPaleteLeituras}</span>
            <span><strong>Pedidos:</strong> ${palete.pedidos.size}</span>
          </div>
        </div>
        ${pedidosHtml}
      </div>
    `;
  }).join('');

  win.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Relatorio de Conferencia</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 20px; color: #1a1a1a; font-size: 11px; }
    h1 { font-size: 18px; margin: 0 0 4px 0; }
    h3 { font-size: 13px; margin: 0; color: #2c3e50; }
    .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 16px; }
    .header .meta { color: #555; font-size: 11px; margin-top: 4px; }
    .resumo { display: flex; gap: 16px; margin-bottom: 16px; padding: 10px; background: #f4f6f8; border-radius: 6px; }
    .resumo-item { font-size: 11px; }
    .resumo-item strong { display: block; font-size: 16px; color: #2c3e50; }
    .palete-bloco { margin-bottom: 16px; border: 1px solid #ccc; border-radius: 6px; page-break-inside: avoid; }
    .palete-header { background: #e8eef4; padding: 8px 12px; border-bottom: 1px solid #ccc; }
    .palete-meta { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 4px; font-size: 10px; color: #555; }
    .pedido-bloco { border-top: 1px solid #ddd; }
    .pedido-header { padding: 6px 12px; background: #fafbfc; display: flex; justify-content: space-between; align-items: center; font-size: 11px; }
    .pedido-count { color: #666; font-size: 10px; }
    .etiquetas-table { width: 100%; border-collapse: collapse; font-size: 10px; }
    .etiquetas-table th { background: #f0f0f0; padding: 5px 8px; text-align: left; border-bottom: 1px solid #ddd; }
    .etiquetas-table td { padding: 4px 8px; border-bottom: 1px solid #eee; }
    .etiquetas-table .mono { font-family: 'Courier New', monospace; font-size: 9px; }
    .filtros { background: #fff8dc; border-left: 3px solid #f1c40f; padding: 6px 10px; margin-bottom: 12px; font-size: 10px; }
    @media print {
      body { margin: 10mm; }
      .no-print { display: none; }
      .palete-bloco { break-inside: avoid; }
    }
    .botoes-acao { text-align: right; margin-bottom: 16px; }
    .botoes-acao button { padding: 8px 16px; background: #3498db; color: #fff; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px; margin-left: 8px; }
    .botoes-acao button.secundario { background: #95a5a6; }
  </style>
</head>
<body>
  <div class="no-print botoes-acao">
    <button onclick="window.print()">Imprimir / Salvar como PDF</button>
    <button class="secundario" onclick="window.close()">Fechar</button>
  </div>

  <div class="header">
    <h1>Relatorio de Conferencia de Carga</h1>
    <div class="meta">
      <span><strong>Periodo:</strong> ${escapeHtml(periodoStr)}</span>
      &nbsp;&middot;&nbsp;
      <span><strong>Gerado em:</strong> ${escapeHtml(dataAtual)}</span>
    </div>
  </div>

  ${filtros && (filtros.cliente || filtros.numero_pedido || filtros.busca) ? `
    <div class="filtros">
      <strong>Filtros aplicados:</strong>
      ${filtros.cliente ? ` Cliente: ${escapeHtml(filtros.cliente)}` : ''}
      ${filtros.numero_pedido ? ` &middot; Nº Pedido: ${escapeHtml(filtros.numero_pedido)}` : ''}
      ${filtros.busca ? ` &middot; Busca: ${escapeHtml(filtros.busca)}` : ''}
    </div>
  ` : ''}

  <div class="resumo">
    <div class="resumo-item"><strong>${totalPaletes}</strong> Paletes</div>
    <div class="resumo-item"><strong>${totalPedidos}</strong> Pedidos</div>
    <div class="resumo-item"><strong>${totalLeituras}</strong> Leituras (etiquetas)</div>
  </div>

  ${totalPaletes === 0
    ? '<p style="text-align:center;color:#888;padding:40px;">Nenhum dado encontrado para os filtros selecionados.</p>'
    : paletesHtml
  }
</body>
</html>`);
  win.document.close();
}
